import { Outlet, RouteObject, createBrowserRouter } from "react-router-dom"
import { AdminDashboardScreen } from "~/admin/admin-dashboard-screen"
import {
  RequireSystemAdminSignedIn,
  RequireUserSignedIn,
  RequireUserSignedOut,
} from "~/auth/auth-layouts"
import { LoginLayout } from "~/auth/login-layout"
import {
  createLettermemePath,
  credentialsLoginPath,
  dashboardPath,
  editLettermemePath,
  emailAuthPath,
  lettermemePath,
  lettermemeLatestPath,
  lettermemeArchivePath,
  lettermemesPath,
  lettermemeNewsletterInputsPath,
  lettermemeSourcesPath,
  lettermemeSubscribedPath,
  lettermemeSubscribersPath,
  lettermemeTrackingCodePath,
  lettermemeWeekInReviewPath,
  loginPath,
  myLettermemesPath,
  myLettermemeManagePath,
  myLettermemeMonetizationPath,
  settingsPath,
  setupLettermemePath,
  stripeOnboardingCompletePath,
  stripeOnboardingRefreshPath,
  rootPath,
} from "~/common/paths"
import { SidebarLayout } from "~/layouts/sidebar-layout"
import { CreateLettermemeScreen } from "~/lettermemes/create-lettermeme-screen"
import { EditLettermemeScreen } from "~/lettermemes/edit-lettermeme-screen"
import { LettermemeSetupScreen } from "~/lettermemes/lettermeme-setup-screen"
import { LettermemeHomeScreen } from "~/lettermemes/lettermeme-home-screen"
import { LettermemeArchiveScreen } from "~/lettermemes/lettermeme-archive-screen"
import { LettermemeScreen } from "~/lettermemes/lettermeme-screen"
import { LettermemeSubscribedScreen } from "~/lettermemes/lettermeme-subscribed-screen"
import { LettermemeTrackingCodeScreen } from "~/lettermemes/lettermeme-tracking-code-screen"
import { LettermemeWeekInReviewScreen } from "~/lettermemes/lettermeme-week-in-review-screen"
import { LettermemesScreen } from "~/lettermemes/lettermemes-screen"
import { MyLettermemeManageScreen } from "~/lettermemes/my-lettermeme-manage-screen"
import { MyLettermemeMonetizationScreen } from "~/lettermemes/my-lettermeme-monetization-screen"
import { MyLettermemesScreen } from "~/lettermemes/my-lettermemes-screen"
import { NewsletterInputsScreen } from "~/newsletter-inputs/newsletter-inputs-screen"
import { SourcesScreen } from "~/sources/sources-screen"
import { SubscribersScreen } from "~/subscribers/subscribers-screen"
import { CredentialsLoginScreen } from "~/login/credentials-login-screen"
import { EmailAuthScreen } from "~/login/email-auth-screen"
import { EmailLoginScreen } from "~/login/email-login-screen"
import { SettingsScreen } from "~/profile/settings-screen"
import { StripeOnboardingCompleteScreen } from "~/stripe/stripe-onboarding-complete-screen"
import { StripeOnboardingRefreshScreen } from "~/stripe/stripe-onboarding-refresh-screen"
import { ErrorBoundary } from "../ui/error-boundary"
import { RootLayout } from "./root-layout"
import { RootScreen } from "./root-screen"

const systemAdminAuthenticatedRoutes: Array<RouteObject> = [
  {
    path: dashboardPath.pattern,
    element: <AdminDashboardScreen />,
  },
]

const authenticatedRoutes: Array<RouteObject> = [
  {
    path: settingsPath.pattern,
    element: <SettingsScreen />,
  },
  {
    path: myLettermemesPath.pattern,
    element: <MyLettermemesScreen />,
  },
  {
    path: myLettermemeManagePath.pattern,
    element: <MyLettermemeManageScreen />,
  },
  {
    path: myLettermemeMonetizationPath.pattern,
    element: <MyLettermemeMonetizationScreen />,
  },
  {
    path: stripeOnboardingCompletePath.pattern,
    element: <StripeOnboardingCompleteScreen />,
  },
  {
    path: stripeOnboardingRefreshPath.pattern,
    element: <StripeOnboardingRefreshScreen />,
  },
  {
    path: createLettermemePath.pattern,
    element: <CreateLettermemeScreen />,
  },
  {
    path: editLettermemePath.pattern,
    element: <EditLettermemeScreen />,
  },
  {
    path: setupLettermemePath.pattern,
    element: <LettermemeSetupScreen />,
  },
  {
    path: lettermemeSourcesPath.pattern,
    element: <SourcesScreen />,
  },
  {
    path: lettermemeSubscribersPath.pattern,
    element: <SubscribersScreen />,
  },
  {
    path: lettermemeNewsletterInputsPath.pattern,
    element: <NewsletterInputsScreen />,
  },
  {
    path: lettermemeTrackingCodePath.pattern,
    element: <LettermemeTrackingCodeScreen />,
  },
]

export const router = createBrowserRouter([
  {
    path: "/",
    element: <RootLayout />,
    errorElement: <ErrorBoundary />,
    children: [
      {
        element: <RequireSystemAdminSignedIn />,
        children: [
          {
            element: <SidebarLayout />,
            children: [
              {
                element: <Outlet />,
                children: [...systemAdminAuthenticatedRoutes],
              },
            ],
          },
        ],
      },
      {
        element: <RequireUserSignedIn />,
        children: [
          {
            element: <Outlet />,
            children: [...authenticatedRoutes],
          },
        ],
      },
      {
        element: <RequireUserSignedOut />,
        children: [
          {
            element: <LoginLayout />,
            children: [
              {
                path: loginPath.pattern,
                element: <EmailLoginScreen />,
              },
              {
                path: credentialsLoginPath.pattern,
                element: <CredentialsLoginScreen />,
              },
              {
                path: emailAuthPath.pattern,
                element: <EmailAuthScreen />,
              },
            ],
          },
        ],
      },
      {
        path: lettermemesPath.pattern,
        element: <LettermemesScreen />,
      },
      {
        path: rootPath.pattern,
        element: <RootScreen />,
      },
      {
        path: lettermemeSubscribedPath.pattern,
        element: <LettermemeSubscribedScreen />,
      },
      {
        path: lettermemeLatestPath.pattern,
        element: <LettermemeScreen />,
      },
      {
        path: lettermemeArchivePath.pattern,
        element: <LettermemeArchiveScreen />,
      },
      {
        path: lettermemeWeekInReviewPath.pattern,
        element: <LettermemeWeekInReviewScreen />,
      },
      {
        path: lettermemePath.pattern,
        element: <LettermemeHomeScreen />,
      },
    ],
  },
])
